"use client";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as React from "react";

import { css, sva } from "@/../styled-system/css";

const dropdown = sva({
  slots: [
    "root",
    "trigger",
    "group",
    "portal",
    "sub",
    "radioGroup",
    "subTrigger",
    "subContent",
    "content",
    "item",
    "itemIndicator",
    "checkboxItem",
    "radioItem",
    "label",
    "separator",
    "shortcut",
  ],
  base: {
    subTrigger: {
      display: "flex",
      cursor: "default",
      userSelect: "none",
      alignItems: "center",
      rounded: "sm",
      px: "2",
      py: "1.5",
      fontSize: "sm",
      lineHeight: "sm",
      ring: "none",
    },
    subContent: {
      zIndex: "50",
      minW: "8rem",
      overflow: "hidden",
      rounded: "md",
      p: "1",
      shadow: "lg",
      "&[data-state=open]": {
        animateIn: true,
        fadeIn: 0,
        zoomIn: 95,
      },
      "&[data-state=closed]": {
        animateOut: true,
        fadeOut: 0,
        zoomOut: 95,
      },
    },
    content: {
      zIndex: "50",
      minW: "200px",
      overflow: "hidden",
      rounded: "md",
      boxShadow: "lg",
      width: "100%",
      backgroundColor: "white",
      px: "2.5",
      pt: "18px",
      pb: "2",
      "&[data-state=open]": {
        animateIn: true,
        fadeIn: 0,
        zoomIn: 95,
      },
      "&[data-state=closed]": {
        animateOut: true,
        fadeOut: 0,
        zoomOut: 95,
      },
    },
    item: {
      position: "relative",
      display: "flex",
      cursor: "pointer",
      userSelect: "none",
      alignItems: "center",
      rounded: "sm",
      px: "3.5",
      py: "15px",
      fontSize: "lg",
      ring: "none",
      transition: "colors",
      transitionDuration: "0.15s",
      _focus: { backgroundColor: "brandSoftTeal" },
      _disabled: { pointerEvents: "none", opacity: "0.5" },
      _hover: {
        backgroundColor: "brandSoftTeal",
      },
    },
    checkboxItem: {
      position: "relative",
      display: "flex",
      cursor: "default",
      userSelect: "none",
      alignItems: "center",
      rounded: "sm",
      pt: "1.5",
      pb: "1.5",
      pl: "8",
      pr: "2",
      fontSize: "sm",
      lineHeight: "sm",
      ring: "none",
      transition: "colors",
      _disabled: { pointerEvents: "none", opacity: "0.5" },
    },
    radioItem: {
      position: "relative",
      display: "flex",
      cursor: "default",
      userSelect: "none",
      alignItems: "center",
      rounded: "sm",
      py: "1.5",
      pl: "2",
      fontSize: "sm",
      lineHeight: "sm",
      ring: "none",
      ringOffset: "none",
      transitionProperty:
        "color, background-color, border-color, text-decoration-color, fill, stroke",
      transitionTimingFunction: "colors",
      transitionDuration: "colors",
      _disabled: { pointerEvents: "none", opacity: "0.5" },
    },
    itemIndicator: {
      position: "absolute",
      left: "2",
      display: "flex",
      h: "3.5",
      w: "3.5",
      alignItems: "center",
      justifyContent: "center",
    },
    label: {
      px: "2",
      py: "1.5",
      fontSize: "sm",
      lineHeight: "sm",
      fontWeight: "semibold",
    },
    separator: { mx: "1.5", h: "1px", backgroundColor: "neutral.300" },
    shortcut: {
      ml: "auto",
      fontSize: "xs",
      lineHeight: "xs",
      letterSpacing: "widest",
      opacity: "0.6",
    },
  },
});

const {
  subTrigger,
  subContent,
  content,
  item,
  checkboxItem,
  radioItem,
  label,
  separator,
  shortcut,
} = dropdown.raw();

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
    css?: object;
  }
>(({ inset, css: cssProp = {}, children, ...props }, ref) => {
  const className = css(subTrigger, inset && { pl: "8" }, cssProp);

  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={className}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.SubTrigger>
  );
});
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(subContent, cssProp);

  return (
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={className}
      {...props}
    />
  );
});
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    css?: object;
  }
>(({ sideOffset = 4, css: cssProp = {}, ...props }, ref) => {
  const className = css(content, cssProp);

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={className}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    css?: object;
  }
>(({ inset, css: cssProp = {}, ...props }, ref) => {
  const className = css(item, inset && { pl: "8" }, cssProp);

  return (
    <DropdownMenuPrimitive.Item ref={ref} className={className} {...props} />
  );
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & {
    css?: object;
  }
>(({ css: cssProp = {}, children, checked, ...props }, ref) => {
  const className = css(checkboxItem, cssProp);

  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={className}
      checked={checked}
      {...props}
    >
      <span
        className={css({
          position: "absolute",
          left: "2",
          display: "flex",
          h: "3.5",
          w: "3.5",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        <DropdownMenuPrimitive.ItemIndicator>
          {/* <Check className={css({ h: "4", w: "4" })} /> */}
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
});
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & {
    css?: object;
  }
>(({ css: cssProp = {}, children, ...props }, ref) => {
  const className = css(radioItem, cssProp);

  return (
    <DropdownMenuPrimitive.RadioItem ref={ref} className={className} {...props}>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
    css?: object;
  }
>(({ inset, css: cssProp = {}, ...props }, ref) => {
  const className = css(label, inset && { pl: "8" }, cssProp);

  return (
    <DropdownMenuPrimitive.Label ref={ref} className={className} {...props} />
  );
});
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(separator, cssProp);

  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={className}
      {...props}
    />
  );
});
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  css: cssProp = {},
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { css?: object }) => {
  const className = css(shortcut, cssProp);

  return <span className={className} {...props} />;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
